import * as React from 'react';
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { COLORS } from '../styles';

type TabLinkProps = {
  disabled?: boolean;
  requiredLine: string;
  optionalLine?: string;
  onLinkClick?: () => void;
  testId?: string;
} & NavLinkProps;

type StyledTabLinkProps = {
  disabled?: boolean;
  exact: boolean;
};

const StyledTabLink = styled(NavLink)<StyledTabLinkProps>`
  font-size: 16px;
  display: flex;
  flex-direction: column;

  line-height: 120%;
  color: ${COLORS.neutral_600};

  background-color: ${props => (props.disabled ? COLORS.neutral_100 : 'transparent')};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  border-left: 1px solid transparent;
  text-decoration: none;
  position: relative;
  border-radius: 2px;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  &:focus {
    background-color: ${COLORS.white};
    box-shadow: 0px 4px 12px rgba(46, 127, 161, 0.25), 0px 0px 8px #2e7fa1;
    outline: none;

    &::after {
      background-color: ${COLORS.white};
    }
  }

  &.active {
    background-color: ${COLORS.white};
  }
  &.active:hover {
    background-color: ${COLORS.primary_20};
  }
  &.active:focus {
    background-color: ${COLORS.white};
  }

  &:hover {
    background-color: ${COLORS.primary_20};
    color: ${COLORS.primary_600};
  }
  &:active {
    box-shadow: none;
    background-color: ${COLORS.primary_100};
    color: ${COLORS.primary_800};
  }
  &.active {
    &::after {
      position: absolute;
      content: ' ';
      width: 4px;
      top: 0;
      bottom: 0;
      left: -1px;
      background-color: ${COLORS.primary_500};

      border-top-left-radius: 2px;
      border-top-right-radius: 2px;
      border-bottom-left-radius: 2px;
      border-bottom-right-radius: 2px;
    }
  }
  &.active:hover {
    &::after {
      background-color: ${COLORS.primary_600};
    }
  }
  &.active:active {
    background-color: ${COLORS.primary_100};

    &::after {
      background-color: ${COLORS.primary_800};
    }
  }
  &:disabled {
    box-shadow: none;
    background-color: ${COLORS.white};
    span {
      color: ${COLORS.neutral_300};
    }
    &::after {
      background-color: ${COLORS.white};
    }
  }
`;

const TabLink = ({ to, disabled = false, exact = false, requiredLine, optionalLine, onLinkClick, testId }: TabLinkProps) => {
  const location = useLocation();
  const ref = React.useRef<any>(null);

  return (
    <StyledTabLink
      to={to.toString()}
      activeClassName="active"
      disabled={disabled}
      exact={exact}
      role="tab"
      aria-selected={to === location.pathname}
      onClick={() => {
        ref?.current?.blur();
        if (onLinkClick) {
          onLinkClick();
        }
      }}
      ref={ref}
      data-testid={testId}>
      <span>{requiredLine}</span>
      {optionalLine !== undefined && <span>{optionalLine}</span>}
    </StyledTabLink>
  );
};

export default TabLink;
